import React, { ReactElement, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import type { IAppState } from '@/store/reducers'
import { useTranslation } from 'react-i18next'
import ReactDiffViewer from 'react-diff-viewer'
import { IProjectInfo, IChangeApprovalDetail } from '@/store/models/tableEdit.model'

interface ISelectFields {
    field: string | null
    label: string
}

function DiffComp (props: {
    dataObj: IChangeApprovalDetail
}): ReactElement {
    const { dataObj: formDataObj } = props
    const { t } = useTranslation()
    const dicClassList = useSelector((state: IAppState) => state.tableEditData.dicClass)
    const [diffResult, setDiffResult] = useState<{
        newVal: any
        oldVal: any
    } | null>(null)

    // 已选择了的要修改的字段名称
    const selectOptions = useRef<ISelectFields[]>([{
        field: 'techClassId', label: t('Classification')
    }, {
        field: 'researchForms', label: t('researchForms')
    }, {
        field: 'techName', label: t('Technology Name')
    }, {
        field: 'subsidiary', label: t('subsidiary')
    }, {
        field: 'description', label: t('description')
    }, {
        field: 'techLevel', label: t('techLevel')
    }, {
        field: 'priority', label: t('priority')
    }, {
        field: 'techYearQuarters', label: t('year')
    }, {
        field: 'quantitativeIndicators', label: t('quantitativeIndicators')
    }, {
        field: 'tmtapproval', label: t('tmtApproval')
    }, {
        field: 'maturity', label: t('maturity')
    }, {
        field: 'requirementSource', label: t('requirementSource')
    }, {
        field: 'developedBy', label: t('developedBy')
    }, {
        field: 'buStakeholder', label: t('buStakeholder')
    }, {
        field: 'toolsEquipment', label: t('toolsAndEquipment')
    }, {
        field: 'resourceNeeded', label: t('resourceNeeded')
    }])

    const replaceStr = (value: any, key: string) => {
        if (typeof value === 'number' && key !== 'resourceNeeded') {
            switch (key) {
                case 'techLevel':
                    value = [t('core'), t('key'), t('general'), t('normal')][value - 1]
                    break
                default:
                    value = dicClassList[key][value]
            }
        } else if (key === 'techYearQuarters') {
            if(value){
                value?.sort((a: any, b: any) => {
                    if (a.year === b.year) {
                        return a.quarter - b.quarter
                    } else {
                        return a.year - b.year
                    }
                })
                value = value?.map((item: any) => (
                    `${item.year} ${t('year')} ${item.quarter} ${t('quarter')}: ${['PLAN', 'ON'][item.status - 1]}`
                ))
            }
        } else if (typeof value === 'string') {
            if (value.includes('"str":') && value.includes('"obj":')) {
                if ((value.includes('<div') || value.includes('<span'))) {
                    const reg = /<[^>]*>[^<]*(<[^>]*>)?/gi
                    value = JSON.parse(value).str.replace(reg, '')
                } else {
                    value = JSON.parse(value).str
                }
            }
            if (value.includes('\n')){
                value = value.split('\n')
            }
        } else if (value === null) {
            value = ''
        }
        return value
    }

    const showEchoList = () => {
        const techProject: IProjectInfo = {
            [t('Work Group')]: formDataObj.techProject.techClass.workGroup.workGroupName,
            [t('Classification')]: formDataObj.techProject.techClass.classificationName,
            [t('PTN')]: formDataObj.techProject.techPtn,
            ...formDataObj.techProject,
        }
        const fieldArr = ['parent','children','techOnlineReview','auditorStatus','auditorTime','auditor','updator', 'id', 'techClass', 'version', 'createTime', 'creator', 'modifyTime', 'techPtn']
        for (const field of fieldArr) {
            Reflect.deleteProperty(techProject, field)
        }
        const beforeObj: IProjectInfo = {
            ...techProject
        }
        const afterObj: IProjectInfo = {
            ...techProject
        }
        const changeFields = JSON.parse(formDataObj.changeFields)
        changeFields.forEach((item: any) => {
            if(fieldArr.includes(item.field)){
                return
            }
            beforeObj[item.field] = item.before || ''
            afterObj[item.field] = item.after || ''
        })

        // 替换 英文字段名为中文字段名
        for (const key in formDataObj.techProject) {
            const findObj = selectOptions.current.find(item => item.field === key)
            if (findObj) {
                const beforeValue = replaceStr(beforeObj[key], key)
                const afterValue = replaceStr(afterObj[key], key)
                beforeObj[findObj.label] = beforeValue
                afterObj[findObj.label] = afterValue
                Reflect.deleteProperty(beforeObj, key)
                Reflect.deleteProperty(afterObj, key)
            }
        }

        setDiffResult({
            oldVal: JSON.stringify(beforeObj, null, 2),
            newVal: JSON.stringify(afterObj, null, 2),
        })
    }

    useEffect(() => {
        showEchoList()
    }, [formDataObj])

    return diffResult ? (
        <ReactDiffViewer splitView disableWordDiff
            oldValue={diffResult.oldVal}
            newValue={diffResult.newVal}
            leftTitle={<span style={{ marginLeft: 40, fontWeight: 'bold' }}>{t('before')}</span>}
            rightTitle={<span style={{ marginLeft: 40, fontWeight: 'bold' }}>{t('after')}</span>}
            styles={{ 
                contentText: {
                    fontFamily: 'Helvetica Neue, Helvetica, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Arial, sans-serif',
                    color: 'rgba(0, 0, 0, 0.85)'
                }
            }}
        />
    ) : <></>
}

export default DiffComp
